import * as turf from "@turf/turf";
import { GeoBounds, LatLngPair, geoToPixel } from "./aiMappingGeo";

type PixelPoint = [number, number];

const toRings = (polygon: number[]): PixelPoint[] => {
  const ring: PixelPoint[] = [];
  for (let i = 0; i + 1 < polygon.length; i += 2) {
    ring.push([polygon[i], polygon[i + 1]]);
  }
  return ring;
};

const toFlat = (ring: PixelPoint[]): number[] => ring.flatMap(([x, y]) => [x, y]);

const turfPolygonOf = (polygon: number[]) => {
  const ring = toRings(polygon);
  return turf.polygon([[...ring, ring[0]]]);
};

export function pixelPolygonsTouch(a: number[], b: number[]): boolean {
  if (a.length < 6 || b.length < 6) return false;
  try {
    const union = turf.union(turfPolygonOf(a) as any, turfPolygonOf(b) as any);
    return !!union && union.geometry.type === "Polygon";
  } catch {
    return false;
  }
}

export function pixelPolygonArea(polygon: number[]): number {
  const ring = toRings(polygon);
  let area = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    area += x1 * y2 - x2 * y1;
  }
  return Math.abs(area) / 2;
}

export function unionPixelPolygons(polygons: number[][]): number[] | null {
  const features = polygons
    .map(toRings)
    .filter((ring) => ring.length >= 3)
    .map((ring) => turf.polygon([[...ring, ring[0]]]));

  if (!features.length) return null;

  let current: turf.Feature<turf.Polygon | turf.MultiPolygon> | null = features[0];
  for (let i = 1; i < features.length; i += 1) {
    if (!current || current.geometry.type !== "Polygon") return null;
    current = turf.union(current as turf.Feature<turf.Polygon>, features[i]);
    if (!current) return null;
  }

  if (!current || current.geometry.type !== "Polygon") return null;

  const merged = current.geometry.coordinates[0] as PixelPoint[];
  if (merged.length < 4) return null;

  return toFlat(merged.slice(0, -1));
}

export function splitPixelPolygonByLine(
  polygon: number[],
  a: PixelPoint,
  b: PixelPoint
): [number[], number[]] | null {
  if (polygon.length < 6) return null;
  const dir: PixelPoint = [b[0] - a[0], b[1] - a[1]];
  if (dir[0] === 0 && dir[1] === 0) return null;

  const signedDistance = (point: PixelPoint): number =>
    dir[0] * (point[1] - a[1]) - dir[1] * (point[0] - a[0]);

  const clipHalfPlane = (keepPositive: boolean): PixelPoint[] | null => {
    const ring = toRings(polygon);
    const output: PixelPoint[] = [];

    for (let i = 0; i < ring.length; i += 1) {
      const current = ring[i];
      const previous = ring[(i + ring.length - 1) % ring.length];
      const inCurrent = keepPositive ? signedDistance(current) >= 0 : signedDistance(current) <= 0;
      const inPrevious = keepPositive
        ? signedDistance(previous) >= 0
        : signedDistance(previous) <= 0;

      if (inCurrent !== inPrevious) {
        const dCurrent = signedDistance(current);
        const dPrevious = signedDistance(previous);
        const total = Math.abs(dCurrent) + Math.abs(dPrevious);
        const ratio = total === 0 ? 0 : Math.abs(dPrevious) / total;
        output.push([
          previous[0] + (current[0] - previous[0]) * ratio,
          previous[1] + (current[1] - previous[1]) * ratio,
        ]);
      }
      if (inCurrent) {
        output.push(current);
      }
    }

    if (output.length < 3) return null;
    return output;
  };

  const positive = clipHalfPlane(true);
  const negative = clipHalfPlane(false);

  if (!positive || !negative) return null;

  const dedupe = (ring: PixelPoint[]): PixelPoint[] =>
    ring.filter(
      (point, index) =>
        index === 0 ||
        Math.abs(point[0] - ring[index - 1][0]) > 1e-9 ||
        Math.abs(point[1] - ring[index - 1][1]) > 1e-9
    );

  const first = dedupe(positive);
  const second = dedupe(negative);

  if (first.length < 3 || second.length < 3) return null;

  return [toFlat(first), toFlat(second)];
}

export function erasePixelPolygons(
  polygon: number[],
  erasePolygon: number[]
): { fullyErased: boolean; pieces: number[][] } | null {
  const ring = toRings(polygon);
  const eraseRing = toRings(erasePolygon);
  if (ring.length < 3 || eraseRing.length < 3) return null;

  const target = turf.polygon([[...ring, ring[0]]]);
  const eraser = turf.polygon([[...eraseRing, eraseRing[0]]]);

  try {
    if (!turf.intersect(target, eraser)) return null;

    const remainder = turf.difference(target, eraser);
    if (!remainder) return { fullyErased: true, pieces: [] };

    const polygonRings =
      remainder.geometry.type === "Polygon"
        ? [remainder.geometry.coordinates]
        : remainder.geometry.coordinates;

    const pieces = polygonRings
      .map((rings) => rings[0] as number[][])
      .filter((outer) => outer.length >= 4)
      .map((outer) => {
        const ring = outer.slice(0, -1).map(([x, y]) => [x, y] as PixelPoint);
        return { ring, area: turf.area(turf.polygon([[...ring, ring[0]]])) };
      })
      .sort((a, b) => b.area - a.area)
      .map(({ ring }) => toFlat(ring));

    if (!pieces.length) return { fullyErased: true, pieces: [] };
    return { fullyErased: false, pieces };
  } catch {
    return null;
  }
}

export function geoPointsToPixelPolygon(
  points: LatLngPair[],
  imageWidth: number,
  imageHeight: number,
  bounds: GeoBounds
): number[] | null {
  const flat: number[] = [];
  for (const [lat, lng] of points) {
    const pixel = geoToPixel(lat, lng, imageWidth, imageHeight, bounds);
    if (!pixel) return null;
    flat.push(pixel[0], pixel[1]);
  }
  return flat.length >= 6 ? flat : null;
}
